import React, { useState } from 'react';
import { auth } from '../lib/firebase';
import {
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
  reload
} from 'firebase/auth';
import { db } from '../firebase';
import { collection, doc, getDoc, getDocs, limit, query, where } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import NProgress from 'nprogress';

// Ensure we're using the environment variable
const API_URL = '/api';
if (!API_URL) {
  console.error('API URL is not defined');
}

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [resetSent, setResetSent] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [userForVerification, setUserForVerification] = useState(null);
  const [checkingVerification, setCheckingVerification] = useState(false);
  const [confirmationCode, setConfirmationCode] = useState('');
  const [showConfirmationInput, setShowConfirmationInput] = useState(false);
  const navigate = useNavigate();

  const navigateByUserRole = async (user) => {
    let userDoc = await getDoc(doc(db, 'users', user.uid));
    if (!userDoc.exists()) {
      const uidQuery = query(collection(db, 'users'), where('uid', '==', user.uid), limit(1));
      const snapshot = await getDocs(uidQuery);
      if (!snapshot.empty) {
        userDoc = snapshot.docs[0];
      }
    }

    const role = userDoc.exists() ? userDoc.data().role : null;
    if (role === 'parent') {
      navigate('/parent-dashboard');
      return;
    }
    navigate('/dashboard');
  };

  const generateConfirmationCode = () => {
    return Math.floor(100000 + Math.random() * 900000).toString();
  };

  const sendConfirmationEmail = async (email, code) => {
    try {
      const response = await fetch('/api/send-verification', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, code }),
        credentials: 'include',
      });

      let data;
      try {
        data = await response.json();
      } catch {
        console.error('Error parsing server response');
        throw new Error('Server returned an invalid response');
      }

      if (!response.ok) {
        throw new Error(data.message || `Server error: ${response.status}`);
      }

      if (!data.success) {
        throw new Error(data.message || 'Failed to send verification code');
      }

      setShowConfirmationInput(true);
      return true;
    } catch (error) {
      console.error('Error in verification process');
      setError(error.message || 'Failed to send verification code. Please try again.');
      return false;
    }
  };

  const handleEmailAuth = async (e) => {
    e.preventDefault();
    NProgress.start();
    setError('');
    setResetSent(false);
    setUserForVerification(null);
    setVerifying(false);
    setCheckingVerification(false);
    setShowConfirmationInput(false);
    setConfirmationCode('');

    try {
      await signInWithEmailAndPassword(auth, email.trim(), password);

      const code = generateConfirmationCode();
      await sendConfirmationEmail(email, code);

      await auth.signOut();
    } catch (error) {
      setError(error.message);
      NProgress.done();
    }
  };

  const handleConfirmationCode = async (e) => {
    e.preventDefault();
    NProgress.start();
    try {
      const response = await fetch('/api/verify-code', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, code: confirmationCode }),
        credentials: 'include',
      });

      let data;
      try {
        data = await response.json();
      } catch {
        console.error('Error parsing server response');
        throw new Error('Server returned an invalid response');
      }

      if (!response.ok) {
        throw new Error(data.message || `Server error: ${response.status}`);
      }

      if (!data.success) {
        throw new Error(data.message || 'Invalid verification code');
      }

      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      
      await navigateByUserRole(userCredential.user);
    } catch (error) {
      console.error('Error in verification process');
      setError(error.message || 'Failed to verify code. Please try again.');
    } finally {
      NProgress.done();
    }
  };

  const handleForgotPassword = async () => {
    if (!email) {
      setError('Please enter your email address first');
      return;
    }
    NProgress.start();
    try {
      await sendPasswordResetEmail(auth, email.trim());
      setResetSent(true);
      setError('');
    } catch (error) {
      setError(error.message);
    } finally {
      NProgress.done();
    }
  };

  const handleCheckVerification = async () => {
    if (!userForVerification) return;
    NProgress.start();
    setCheckingVerification(true);
    setError('');
    try {
      await reload(userForVerification);
      if (userForVerification.emailVerified) {
        navigate('/dashboard');
      } else {
        setError('Email not verified yet. Please check your inbox and click the verification link.');
        NProgress.done();
      }
    } catch (error) {
      setError(error.message);
      NProgress.done();
    } finally {
      setCheckingVerification(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#1a1f2e] py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8 bg-[#313643] rounded-xl shadow-lg p-8">
        <div>
          <h2 className="text-center text-2xl font-bold text-white mb-2">
            Welcome
          </h2>
          <p className="text-center text-sm text-gray-400 mb-4">
            Sign in to your account
          </p>
        </div>

        {!showConfirmationInput ? (
          <form onSubmit={handleEmailAuth} className="mt-8 space-y-6">
            <div className="rounded-md shadow-sm -space-y-px">
              <div>
                <input
                  type="email"
                  required
                  className="appearance-none rounded-t-md relative block w-full px-3 py-2 border border-gray-700 bg-[#313643] placeholder-gray-500 text-white focus:outline-none focus:ring-2 focus:ring-white focus:border-white sm:text-sm"
                  placeholder="Email address"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  required
                  className="appearance-none rounded-b-md relative block w-full px-3 py-2 border border-gray-700 bg-[#313643] placeholder-gray-500 text-white focus:outline-none focus:ring-2 focus:ring-white focus:border-white sm:text-sm pr-10"
                  placeholder="Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-white transition-colors"
                >
                  {showPassword ? (
                    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                    </svg>
                  ) : (
                    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  )}
                </button>
              </div>
            </div>

            {error && (
              <div className="text-red-400 text-sm text-center">{error}</div>
            )}

            {resetSent && (
              <div className="text-green-400 text-sm text-center">
                Password reset email sent! Please check your inbox.
              </div>
            )}

            <div className="flex items-center justify-between">
              <button
                type="button"
                onClick={handleForgotPassword}
                className="text-sm text-white hover:text-[#00E0E0] transition-colors"
              >
                Forgot your password?
              </button>
            </div>

            <div>
              <button
                type="submit"
                className="group relative w-full flex justify-center items-center py-2 px-4 border border-transparent text-sm font-bold rounded-lg text-white bg-[#545967] hover:bg-[#6b7280] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#545967] shadow-md transition-colors"
              >
                Proceed
                <svg className="ml-2 w-5 h-5 text-white group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </button>
            </div>
          </form>
        ) : (
          <form onSubmit={handleConfirmationCode} className="mt-8 space-y-6">
            <div>
              <p className="text-white text-sm text-center mb-4">
                A confirmation code has been sent to your email address.
                Please enter the 6-digit code below.
              </p>
              <input
                type="text"
                required
                maxLength="6"
                pattern="[0-9]{6}"
                className="appearance-none rounded-md relative block w-full px-3 py-2 border border-gray-700 bg-[#313643] placeholder-gray-500 text-white focus:outline-none focus:ring-2 focus:ring-white focus:border-white sm:text-sm text-center tracking-widest"
                placeholder="Enter 6-digit code"
                value={confirmationCode}
                onChange={(e) => setConfirmationCode(e.target.value.replace(/[^0-9]/g, ''))}
              />
            </div>

            {error && (
              <div className="text-red-400 text-sm text-center">{error}</div>
            )}

            <div>
              <button
                type="submit"
                className="group relative w-full flex justify-center items-center py-2 px-4 border border-transparent text-sm font-bold rounded-lg text-white bg-[#545967] hover:bg-[#6b7280] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#545967] shadow-md transition-colors"
              >
                Verify Code
                <svg className="ml-2 w-5 h-5 text-white group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </button>
            </div>
          </form>
        )}

        {verifying && (
          <div className="mt-6 flex flex-col items-center">
            <div className="text-sm text-white text-center mb-2">
              Please verify your email address. A verification link has been sent to your email.<br />
              After verifying, click the button below.
            </div>
            <button
              onClick={handleCheckVerification}
              disabled={checkingVerification}
              className={`mt-2 px-4 py-2 rounded bg-[#545967] text-white font-bold hover:bg-[#6b7280] transition-colors ${checkingVerification ? 'opacity-60 cursor-not-allowed' : ''}`}
            >
              {checkingVerification ? 'Checking...' : 'I have verified my email'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Login; 